import React, { useState } from "react";
import { Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

import { HubHookFormWrapper, PageContent } from "../../components";
import {
  useGetLocationGoals,
  usePutLocationGoals,
  useGetClosedDates,
  useAddClosedDate,
  useRemoveClosedDate,
} from "../../hooks/react-query/useCalendarData";
import { getUserLocation, getUserPermissionById } from "../../redux/auth";
import { PERMISSIONS_KEY } from "../../constants/permissions";

import CalendarGoalsForm from "./CalendarGoalsForm";
import CalendarClosedDays from "./CalendarClosedDays";
import { StyledContentStack } from "./styles";

const yearStart = dayjs().startOf("year").toISOString();
const yearEnd = dayjs().endOf("year").toISOString();

const isWeekend = (date) => {
  const day = dayjs(date).day();
  return day === 0 || day === 6;
};

const CalendarGoalsContainer = () => {
  const defaultLocation = useSelector(getUserLocation);
  const canEdit = useSelector((state) =>
    getUserPermissionById(state, PERMISSIONS_KEY.CALENDAR, 2),
  );

  const [locationId, setLocationId] = useState(defaultLocation || 1);

  const { data: goals, isLoading, isSuccess } = useGetLocationGoals(locationId);
  const { mutate: updateGoals, isLoading: isSaving } = usePutLocationGoals();

  const { data: closedDates = [] } = useGetClosedDates({
    location: locationId,
    startDate: yearStart,
    endDate: yearEnd,
  });
  const { mutate: addClosed } = useAddClosedDate();
  const { mutate: removeClosed } = useRemoveClosedDate();

  const closedSet = new Set(
    closedDates.map((d) => dayjs(d.date).utc().format("YYYY-MM-DD")),
  );

  const handleSubmit = (data) => {
    updateGoals({
      locationId,
      revenueGoal: Number(data.revenueGoal),
      waxHourGoal: Number(data.waxHourGoal),
    });
  };

  const handleDateClick = (date) => {
    if (!canEdit || isWeekend(date)) return;
    const key = dayjs(date).utc().format("YYYY-MM-DD");
    const existing = closedDates.find(
      (d) => dayjs(d.date).utc().format("YYYY-MM-DD") === key,
    );
    if (existing) removeClosed(existing.id);
    else
      addClosed({
        location: locationId,
        date: dayjs(date).utc().toISOString(),
      });
  };

  const defaultValues = {
    revenueGoal: goals?.revenueGoal ?? 0,
    waxHourGoal: goals?.waxHourGoal ?? 0,
  };

  return (
    <HubHookFormWrapper onSubmit={handleSubmit} defaultValues={defaultValues}>
      <PageContent smallPadding>
        <StyledContentStack>
          <CalendarClosedDays
            closedDates={closedDates}
            closedSet={closedSet}
            canEdit={canEdit}
            onDateClick={handleDateClick}
            onRemove={removeClosed}
          />
          <CalendarGoalsForm
            locationId={locationId}
            setLocationId={setLocationId}
            canEdit={canEdit}
            isLoading={isLoading}
            isSaving={isSaving}
          />
        </StyledContentStack>
      </PageContent>
    </HubHookFormWrapper>
  );
};

export default CalendarGoalsContainer;
